import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import Header from '../components/Header';

function TemplateEditor({ template, onSave, onCancel }) {
  const [name, setName] = useState(template?.name ?? '');
  const [type, setType] = useState(template?.type ?? 'custom');
  const [sections, setSections] = useState(template?.items ?? [{ title: '', items: [''] }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const addSection = () =>
    setSections((prev) => [...prev, { title: '', items: [''] }]);

  const removeSection = (sIdx) =>
    setSections((prev) => prev.filter((_, i) => i !== sIdx));

  const updateSectionTitle = (sIdx, value) =>
    setSections((prev) =>
      prev.map((s, i) => (i === sIdx ? { ...s, title: value } : s))
    );

  const addItem = (sIdx) =>
    setSections((prev) =>
      prev.map((s, i) =>
        i === sIdx ? { ...s, items: [...s.items, ''] } : s
      )
    );

  const removeItem = (sIdx, iIdx) =>
    setSections((prev) =>
      prev.map((s, i) =>
        i === sIdx ? { ...s, items: s.items.filter((_, j) => j !== iIdx) } : s
      )
    );

  const updateItem = (sIdx, iIdx, value) =>
    setSections((prev) =>
      prev.map((s, i) =>
        i === sIdx
          ? { ...s, items: s.items.map((item, j) => (j === iIdx ? value : item)) }
          : s
      )
    );

  const handleSave = async () => {
    if (!name.trim()) { setError('Template name is required.'); return; }
    const hasEmptySection = sections.some((s) => !s.title.trim());
    if (hasEmptySection) { setError('All sections need a title.'); return; }
    const hasEmptyItem = sections.some((s) => s.items.some((item) => !item.trim()));
    if (hasEmptyItem) { setError('All checklist items must have text.'); return; }

    setSaving(true);
    setError('');

    const cleaned = sections.map((s) => ({
      title: s.title.trim(),
      items: s.items.map((item) => item.trim()).filter(Boolean),
    }));

    await onSave({ name: name.trim(), type: type.trim() || 'custom', items: cleaned });
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={onCancel} className="text-orange-400 hover:text-orange-300 text-sm font-medium">
          ← Cancel
        </button>
        <h1 className="text-white font-semibold flex-1">
          {template ? 'Edit Template' : 'New Template'}
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-xl"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5 pb-16">
        {error && (
          <div className="bg-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 border border-red-500/30">
            {error}
          </div>
        )}

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Template Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="e.g. Site Induction Checklist"
              className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5">Type / Category</label>
            <input
              type="text"
              value={type}
              onChange={(e) => setType(e.target.value)}
              maxLength={50}
              placeholder="e.g. site_induction"
              className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        {sections.map((section, sIdx) => (
          <div key={sIdx} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="bg-gray-700/50 px-4 py-3 flex items-center gap-2">
              <input
                type="text"
                value={section.title}
                onChange={(e) => updateSectionTitle(sIdx, e.target.value)}
                placeholder={`Section ${sIdx + 1} title`}
                maxLength={100}
                className="flex-1 bg-transparent text-orange-400 font-semibold text-sm focus:outline-none placeholder-gray-600 uppercase"
              />
              {sections.length > 1 && (
                <button
                  onClick={() => removeSection(sIdx)}
                  className="text-gray-600 hover:text-red-400 text-xs"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="p-4 space-y-2">
              {section.items.map((item, iIdx) => (
                <div key={iIdx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateItem(sIdx, iIdx, e.target.value)}
                    placeholder={`Item ${iIdx + 1}`}
                    maxLength={200}
                    className="flex-1 bg-gray-700 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 placeholder-gray-600"
                  />
                  {section.items.length > 1 && (
                    <button
                      onClick={() => removeItem(sIdx, iIdx)}
                      className="text-gray-600 hover:text-red-400 text-lg leading-none shrink-0"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}

              <button
                onClick={() => addItem(sIdx)}
                className="text-orange-400 hover:text-orange-300 text-sm mt-1"
              >
                + Add item
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={addSection}
          className="w-full border-2 border-dashed border-gray-600 hover:border-orange-500 text-gray-500 hover:text-orange-400 font-medium py-3 rounded-xl text-sm transition-colors"
        >
          + Add Section
        </button>
      </div>
    </div>
  );
}

export default function AdminTemplates() {
  const { user } = useAuth();
  const { membership } = useOrg();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | template object
  const [error, setError] = useState('');

  const fetchTemplates = async () => {
    const { data } = await supabase
      .from('checklist_templates')
      .select('id, name, type, created_at')
      .eq('org_id', membership.org_id)
      .order('created_at', { ascending: false });
    if (data) setTemplates(data);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (membership?.org_id) fetchTemplates();
  }, [membership?.org_id]);

  const handleSave = async (fields) => {
    setError('');
    if (editing === 'new') {
      const { error: e } = await supabase
        .from('checklist_templates')
        .insert({ ...fields, org_id: membership.org_id, created_by: user.id });
      if (e) { setError(e.message); return; }
    } else {
      const { error: e } = await supabase
        .from('checklist_templates')
        .update(fields)
        .eq('id', editing.id);
      if (e) { setError(e.message); return; }
    }
    setEditing(null);
    fetchTemplates();
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    await supabase.from('checklist_templates').delete().eq('id', template.id);
    fetchTemplates();
  };

  const openEdit = async (template) => {
    const { data } = await supabase
      .from('checklist_templates')
      .select('*')
      .eq('id', template.id)
      .single();
    if (data) setEditing(data);
  };

  if (editing) {
    return (
      <TemplateEditor
        template={editing === 'new' ? null : editing}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header title="Templates" backTo="/admin" />

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {error && (
          <div className="bg-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 border border-red-500/30">
            {error}
          </div>
        )}

        <button
          onClick={() => setEditing('new')}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-colors"
        >
          + Create Template
        </button>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-500 text-xs">
            Built-in templates (Vehicle Pre-Start, Pre-Drill, Drill Rig Safety) are always available
            and don't appear here. Custom templates you create will appear on the dashboard
            alongside the built-in types.
          </p>
        </div>

        {loading ? (
          <p className="text-gray-500 text-center py-8">Loading…</p>
        ) : templates.length === 0 ? (
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
            <p className="text-gray-500">No custom templates yet.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {templates.map((tpl) => (
              <div key={tpl.id} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-white font-medium">{tpl.name}</p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      Type: {tpl.type} · Created {new Date(tpl.created_at).toLocaleDateString('en-AU')}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => openEdit(tpl)}
                      className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded-lg bg-gray-700 hover:bg-gray-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(tpl)}
                      className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded-lg bg-gray-700 hover:bg-gray-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
